import React, { useEffect, useState, useCallback } from "react";
import ReusableMuiTable from "../../../components/common/Table/Table";
import Box from "@mui/material/Box";
import { IconButton, Tooltip, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaArrowLeftLong } from "react-icons/fa6";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProduct,
  deleteProductListType
} from "../../../api/service/products.service";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";
import Loader from "../../../components/Loader/Loader";

const CategoryProduct = () => {
  const navigate = useNavigate();
  const { id: typeId } = useParams();
  const [productlist, setProductlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");

  const fetchProductTypelist = useCallback(async () => {
    try {
      setLoading(true);

      if (!typeId) {
        console.error("Type ID is missing");
        showToast.error("Product ID is missing");
        return;
      }

      const res = await getProduct(typeId, page, 10);



      const paginationData = res?.data?.data || {};
      const productsData = paginationData.data || [];
      const lastPage = paginationData.last_page || 1;
      const total = paginationData.total || 0;

      // console.log(productsData, "productsData.....");
      // console.log("Total products:", total);
      // console.log("Last page:", lastPage);

      setProductlist(Array.isArray(productsData) ? productsData : []);
      setTotalPages(lastPage);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      showToast.error(error.response?.data?.message || "Failed to fetch products");
      setProductlist([]);
    } finally {
      setLoading(false);
    }
  }, [page, typeId]);

  useEffect(() => {
    fetchProductTypelist();
  }, [fetchProductTypelist]);



  const handleEdit = (rowData) => navigate(`/update-product/${rowData.id}`);

  const handleDelete = useCallback(
    (rowData) => {
      const id = rowData.id || rowData._id;

      Swal.fire({
        title: "Are you sure to delete this product?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#C39B22",
        cancelButtonColor: "#d32f2f",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteProductListType(id);

            Swal.fire({
              title: "Deleted!",
              text: "Product type has been deleted successfully.",
              icon: "success",
              confirmButtonColor: "#C39B22",
            });

            fetchProductTypelist();
            showToast.success("Product type deleted successfully");
          } catch (error) {
            console.error("Delete error:", error);
            showToast.error(
              error.response?.data?.message || "Failed to delete the product type. Please try again."
            );

            Swal.fire({
              title: "Error!",
              text: error.response?.data?.message || "Something went wrong while deleting.",
              icon: "error",
              confirmButtonColor: "#C39B22",
            });
          }
        }
      });
    },
    [fetchProductTypelist]
  );

  const columns = [
    {
      name: "title",
      label: "Product Title",
    },
    {
      name: "sub_title",
      label: "Product Subtitle",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = productlist[tableMeta.rowIndex];
          if (!row) return "";
          return (
            <Box sx={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row?.sub_title || ""}
            </Box>
          );
        },
      },
    },


    {
      name: "benifits",
      label: "Product Benefits",
      options: {
        customBodyRender: (_, tableMeta) => {
          const row = productlist[tableMeta.rowIndex];
          if (!row) return "-";

          const count = row.benefits_count ?? 0;

          return (
            <Box
              onClick={() => navigate(`/product-benifit/${row.id}`)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "#C39B22",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#b18a1f",
                },
              }}
            >
              {count}
            </Box>
          );
        },
      },
    },

    {
      name: "specifications",
      label: "Product Specifications",
      options: {
        customBodyRender: (_, tableMeta) => {
          const row = productlist[tableMeta.rowIndex];
          if (!row) return "-";

          const count = row.specifications_count ?? 0;

          return (
            <Box
              onClick={() => navigate(`/product-specification/${row.id}`)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "#C39B22",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#b18a1f",
                },
              }}
            >
              {count}
            </Box>
          );
        },
      },
    },

    {
      name: "application_of_use",
      label: "Product Application",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = productlist[tableMeta.rowIndex];
          if (!row) return "";
          return (
            <Box sx={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row?.application_of_use || ""}
            </Box>
          );
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        customBodyRender: (_, tableMeta) => {
          const rowData = productlist[tableMeta.rowIndex];
          if (!rowData) return null;
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(rowData)} sx={{ color: "#C39B22" }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => handleDelete(rowData)} sx={{ color: "#d32f2f" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  if (loading) {
    return (
      <>
        <Loader />
      </>
    );
  }

  return (
    <ReusableMuiTable
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FaArrowLeftLong
            style={{ cursor: "pointer", color: "#C39B22" }}
            onClick={() => navigate(-1)}
            size={20}
          />
          <span>Product List</span>
        </Box>
      }
      data={productlist}
      columns={columns}
      options={{
        page: page - 1,
        count: totalPages * 10,
        rowsPerPage: 10,
        onChangePage: (newPage) => setPage(newPage + 1),
        selectableRows: 'none',
        filterType: 'checkbox',
        searchText: searchText,
        onSearchChange: (text) => {
          setSearchText(text);
          setPage(1);
        },
      }}
      customToolbarRight={
        <Button
          variant="contained"
          onClick={() => navigate(`/add-product/${typeId}`)}
          sx={{
            backgroundColor: "#C39B22",
            textTransform: "none",
            fontWeight: 600,
            height: "36px",
            "&:hover": {
              backgroundColor: "#b18a1f",
            },
          }}
        >
          Add Product
        </Button>
      }
    />
  );
};

export default CategoryProduct;